import React, { useState, useMemo, useCallback } from "react";
import { observer } from "mobx-react-lite";

import useThemeService from "../../../services/theme-service/ThemeService";
import {
  ApplicationError,
  errorHandler,
} from "../../../services/error-handler/ErrorHandlerService";
import {
  UploadSewerObjects,
  UploadSewerObjectsToProjectAssignedTo,
} from "../../../services/authorization/Permissions";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";
import { translationService } from "../../../services/translation/TranslationService";
import { FileExtended } from "../../../services/upload/FileUploadTracker";
import { InspectionFile } from "../../../services/inspection/InspectionFile";
import { InspectionFilesStorage } from "../../../services/inspection/InspectionFilesStorage";
import { snackbarNotificationsService } from "../../../services/notifications/SnackbarNotificationsService";
import Button from "../../../components/button/Button";
import { Upload as UploadSvg } from "../../../components/svg/svgImageList";
import { Dialog } from "../../../components/dialog/Dialog";
import DropFiles from "../../../components/upload-drop-files/DropFiles";
import UploadTable from "../../../components/upload-drop-files/UploadTable";

export default observer(({ inspectionId, inspectionFilesStorage }: {
  inspectionId: number,
  inspectionFilesStorage: InspectionFilesStorage
}) => {
  const { themeClass } = useThemeService();

  const fileToUpdate = useMemo(() => (inspectionFilesStorage.selectedItemsCount === 1
    ? inspectionFilesStorage.data.items.find((f: InspectionFile) => Number(f.id) === (Number(inspectionFilesStorage.selectedItems[0])))
    : null),
    [inspectionFilesStorage.data, inspectionFilesStorage.selectedItems, inspectionFilesStorage.selectedItemsCount])
  const [fileToUpload, setFileToUpload] = useState<FileExtended | null>(null);

  const isUpdateDisabled = useMemo(() => (!fileToUpdate ||
    (!permissionsService.userHasPermissionTo(UploadSewerObjects)
      && !permissionsService.userHasPermissionTo(UploadSewerObjectsToProjectAssignedTo))
  ), [fileToUpdate]);

  const isUploadDisabled = useMemo(() => isUpdateDisabled || !fileToUpload,
    [fileToUpload, isUpdateDisabled]);

  const fileType = useMemo(() => fileToUpdate?.fileName?.substring(fileToUpdate.fileName.lastIndexOf(".")),
    [fileToUpdate?.fileName]);

  const [isUploadModalVisible, setUploadModalVisible] = useState(false);
  const handleDialogClose = useCallback(() => {
    setUploadModalVisible(false);
    setFileToUpload(null);
  }, []);

  const addFilesForUpload = useCallback((newFile: Array<FileExtended>) => {
    if (newFile.length !== 1 || newFile[0].name !== fileToUpdate?.fileName) {
      snackbarNotificationsService.showError("general.upload.inspection-filename-not-match");
      return;
    }

    setFileToUpload(newFile[0]);
  }, [fileToUpdate?.fileName]);

  const removeFile = useCallback(() => setFileToUpload(null), []);

  const handleUploadClick = useCallback(async () => {
    try {
      await inspectionFilesStorage.uploadService.updateMediaFile(fileToUpload!, inspectionId, fileToUpdate!.id);
      inspectionFilesStorage.deselectAll();
      await inspectionFilesStorage.load();
      inspectionFilesStorage.resetSelectionState();
    }
    catch (error) {
      const applicationError = error as ApplicationError;
      errorHandler.handleErrorInSnackbar(applicationError);
    }
  }, [fileToUpdate, fileToUpload, inspectionFilesStorage, inspectionId]);

  return (
    <>
      <Button
        disabled={isUpdateDisabled}
        id="button-update-file"
        icon={{
          svg: UploadSvg,
          textKey: "general.update.file",
          textClass: "actions-header-button-label",
          className: themeClass(["icon-upload"]),
        }}
        onClick={() => setUploadModalVisible(true)}
      />
      <Dialog
        sidePosition
        isVisible={isUploadModalVisible}
        onHide={handleDialogClose}
        titleKey="general.update.file"
        buttonOk={{
          textKey: "general.upload",
          action: handleUploadClick,
          disabled: isUploadDisabled,
        }}
        buttonCancel={{
          textKey: "general.cancel",
          action: handleDialogClose,
        }}
      >
        <div className="upload-modal-content">
          <div className="upload-details">
            {translationService.translate("UpdateFileToInspection.upload-modal-details")}
          </div>
          <DropFiles onDroppedFiles={addFilesForUpload} fileTypesAccepted={fileType} singleFile />
          {fileToUpload && (
            <UploadTable files={[fileToUpload]} onRemoveFile={removeFile} />
          )}
        </div>
      </Dialog>
    </>
  );
});
